import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  createContext,
} from "react";
import queryString from "query-string";
import {
  getAuth,
  onAuthStateChanged,
  signOut as authSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  GithubAuthProvider,
  checkActionCode,
  applyActionCode,
  getAdditionalUserInfo,
  updateEmail as authUpdateEmail,
  updateProfile as authUpdateProfile,
  updatePassword as authUpdatePassword,
  sendPasswordResetEmail as authSendPasswordResetEmail,
  confirmPasswordReset as authConfirmPasswordReset,
} from "firebase/auth";
import { firebaseApp } from "./firebase";
import {
  useUser,
  createUser,
  updateUser,
  fetchCurrentUser,
  sendVerificationEmail,
} from "./db";
import {
  rememberPendingSignup,
  readPendingSignup,
  clearPendingSignup,
  resolveCompanyName,
} from "./pendingSignup";
import { client } from "../constants/api";
import { useNavigate } from "react-router-dom";
import PageLoader from "../components/Loaders/PageLoader";
import analytics from "./analytics";
import { useSearchParams } from "react-router-dom";

// Whether to merge extra user data from database into `auth.user`
const MERGE_DB_USER = true;
// Whether to send email verification on signup.
//
// SOUND-715: ON. This was dead scaffolding — a `false` with a note about a
// welcome-email flow that never shipped. It is now load-bearing: the API
// refuses to create a company or a blank application for a self-serve signup
// whose address is unconfirmed, so an unverified account leaves NO CRM record
// behind. That is what stops bot signups polluting /companies.
const EMAIL_VERIFICATION = true;

// Refusals from POST /users that the person can recover from, as opposed to a
// failure that means the account can never be provisioned. The distinction
// matters because the unrecoverable path signs them out (the API has already
// deleted the Firebase user by then) while these MUST keep the session: the
// account is the very thing holding the unclicked verification link.
const RETRYABLE_SIGNUP_CODES = new Set([
  "EMAIL_VERIFICATION_REQUIRED",
  "CHALLENGE_FAILED",
  // SOUND-726. Until 2026-08-28 the API answered a missing business name by
  // DELETING the Firebase account, so there was nothing left to retry with —
  // the merchant's next sign-in read "invalid credentials" and their signup was
  // simply gone. It is a question now, and the account survives to be asked.
  "COMPANY_NAME_REQUIRED",
]);

// Whether to connect analytics session to `user.uid`
const ANALYTICS_IDENTIFY = true;

// Initialize Firebase auth
const auth = getAuth(firebaseApp);

// Create a `useAuth` hook and `AuthProvider` that enables
// any component to subscribe to auth and re-render when it changes.
const authContext = createContext();
export const useAuth = () => useContext(authContext);
// This should wrap the app in `src/pages/_app.js`
export function AuthProvider({ children }) {
  const auth = useAuthProvider();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook that creates the `auth` object and handles state
// This is called from `AuthProvider` above (extracted out for readability)
function useAuthProvider() {
  // Store auth user in state
  // `user` will be object, `null` (loading) or `false` (logged out)
  const [user, setUser] = useState(null);
  //This throws errors about useLocation may only be used in the context of a router
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get("invite") || null;
  // Invitation tokens are 64 hex chars
  const invitationToken = inviteParam && inviteParam.length === 64 ? inviteParam : null;
  // Merge extra user data from the database
  // This means extra user data (such as payment plan) is available as part
  // of `auth.user` and doesn't need to be fetched separately. Convenient!
  let finalUser = useMergeExtraData(user, { enabled: MERGE_DB_USER });

  // Add custom fields and formatting to the `user` object
  finalUser = useFormatUser(finalUser);

  // Connect analytics session to user
  useIdentifyUser(finalUser, { enabled: ANALYTICS_IDENTIFY });

  // Provision the SERVER-SIDE account for the currently signed-in Firebase user.
  //
  // SOUND-715 split this out of handleAuth because it is no longer a one-shot
  // step at signup. The API refuses to create a company for an unconfirmed
  // address, so this runs again — unchanged — once the person clicks the link,
  // and again on a later sign-in if they never came back.
  const provisionAccount = async ({ fbUser, companyName, turnstileToken } = {}) => {
    const current = fbUser || auth.currentUser;
    if (!current) throw new Error("Not signed in");

    // Pick up a verification that happened in another tab or on a phone. The
    // ID token still says email_verified:false until it is refreshed, which is
    // exactly the case this flow creates.
    await current.reload();

    // SOUND-726: this was `companyName ?? remembered?.companyName`, and `??`
    // only falls through on null/undefined — so an empty string from a
    // rendered-but-untouched input BEAT a good remembered name and the request
    // went out nameless, which was the destructive case. resolveCompanyName
    // trims first, then takes the first thing that is actually a name.
    const remembered = readPendingSignup(current.uid);
    const name = resolveCompanyName({
      typed: companyName,
      remembered: remembered?.companyName,
    });

    const userPayload = {
      email: current.email,
      ...(invitationToken && { invitationToken }),
      // SOUND-631: only sent on self-serve signup. An invited user joins an
      // existing company, so a name here would be meaningless at best.
      ...(name && { companyName: name }),
      ...(turnstileToken && { turnstileToken }),
    };

    await createUser(current.uid, userPayload, {
      id: current.uid,
      // Force a refresh so the API sees a token minted AFTER verification.
      accessToken: await current.getIdToken(true),
    });

    clearPendingSignup();
    setUser(current);
    return current;
  };

  // SOUND-715. Goes through OUR API, not the Firebase client SDK.
  //
  // sendEmailVerification() delivers Firebase's default message — no logo, no
  // branding, a bare action URL on a grey page. The API mints the same link
  // with the Admin SDK and renders templates/email-verification.ejs around it,
  // exactly as password resets already work.
  //
  // It sends no address: the server reads it off the authenticated account, so
  // this cannot be aimed at anybody else.
  const requestVerificationEmail = async (fbUser, turnstileToken) => {
    const current = fbUser || auth.currentUser;
    if (!current) throw new Error("Not signed in");
    await sendVerificationEmail(
      { id: current.uid, accessToken: await current.getIdToken() },
      turnstileToken
    );
  };

  const resendVerification = (turnstileToken) =>
    requestVerificationEmail(null, turnstileToken);

  /**
   * Re-read verification state from Firebase.
   *
   * NOT the authority, and no longer on the signup critical path — the server
   * re-checks on every POST /users and that is the check that gates the data.
   * Kept because reload() alone proved unreliable in DEV (the backend read
   * verified while the client did not), so anything that still wants a local
   * hint also forces a token refresh rather than trusting reload() by itself.
   *
   * @returns {Promise<boolean>}
   */
  const refreshVerification = async () => {
    const current = auth.currentUser;
    if (!current) return false;
    await current.reload();
    if (current.emailVerified) return true;
    try {
      // Force a fresh ID token, then look again. reload() can be served from a
      // cached account record; a forced refresh cannot.
      await current.getIdToken(true);
      await current.reload();
    } catch {
      // Offline or revoked — report what we last saw rather than throwing.
    }
    return Boolean(current.emailVerified);
  };

  // Turn an axios failure from POST /users into either a retryable error the
  // form can act on, or a fatal one that ends the session.
  const handleProvisionFailure = async (error) => {
    const code = error?.response?.data?.code;
    const reason =
      error?.response?.data?.error || "We could not finish creating your account.";

    if (RETRYABLE_SIGNUP_CODES.has(code)) {
      // Keep the session and the Firebase account. The person is mid-flow, not
      // rejected — signing them out here would strand the verification link.
      const retryable = new Error(reason);
      retryable.code = code;
      retryable.cause = error;
      throw retryable;
    }

    // SOUND-709. This used to swallow the error and fall through to setUser(),
    // which signed the person into a session whose server-side account DOES NOT
    // EXIST — the API's failClosedSignup has already deleted the Firebase user
    // by this point. Every subsequent call then 401s or 404s ("User not
    // found"), and the UI looks broken rather than rejected.
    console.error("[handleAuth] Error during new user setup:", error);
    clearPendingSignup();
    try {
      await authSignOut(auth);
    } catch {
      // Already gone server-side; nothing to clean up.
    }
    const wrapped = new Error(reason);
    wrapped.cause = error;
    throw wrapped;
  };

  // Handle response from auth functions (`signup`, `signin`, and `signinWithProvider`)
  const handleAuth = async (response, { companyName, turnstileToken } = {}) => {
    const { user } = response;
    const { isNewUser } = getAdditionalUserInfo(response);

    // Ensure Firebase user is ready before we continue
    await waitForFirebase(user.uid);

    if (isNewUser) {
      rememberPendingSignup(user.uid, companyName);

      // NO separate send here any more.
      //
      // SOUND-722: this called /users/send-verification just before
      // provisionAccount, and once both endpoints demanded a challenge the
      // signup flow spent ONE token on TWO calls. Turnstile tokens are
      // single-use, so the second always failed `timeout-or-duplicate` and
      // every real signup was told it could not be verified as human.
      //
      // POST /users now sends the confirmation email itself as part of its
      // EMAIL_VERIFICATION_REQUIRED refusal, so one token covers one call.
      // requestVerificationEmail survives for the panel's Resend, which has
      // its own widget and therefore its own token.

      try {
        await provisionAccount({ fbUser: user, companyName, turnstileToken });
      } catch (error) {
        return handleProvisionFailure(error);
      }
    } else {
      // Sign-in. "Authenticated but never provisioned" is now a reachable
      // state: someone signs up, does not confirm, and comes back later. The
      // Firebase account survives, so they can neither sign up again (the
      // address is taken) nor use the app (no user document). Without this they
      // would loop on a 401 with no way forward.
      const existing = await fetchCurrentUser({
        id: user.uid,
        accessToken: await user.getIdToken(),
      });

      if (!existing) {
        // SOUND-722. Check verification BEFORE calling the API.
        //
        // There is no Turnstile widget on the sign-in form, and POST /users now
        // treats a missing token as a bot tell. Calling it unverified from here
        // would manufacture a false CHALLENGE_FAILED on every legitimate resume
        // — precisely the noise that destroys the signal. So decide locally:
        // unverified means show the panel (which HAS a widget) and send
        // nothing; verified means the server skips the challenge entirely,
        // because that branch only runs for unconfirmed addresses.
        //
        // Safe to decide locally because nothing is created here — the server
        // still re-checks before it writes anything.
        //
        // SOUND-752: except on an invitation. POST /users does not ask an
        // invited user for a challenge or a confirmed address (the invitation
        // branch proves address control), so gating it here would strand
        // someone whose account predates their invite on the verify panel.
        // AuthSection drops a dead token from the URL before the form renders,
        // so a token here is one that validated.
        await user.reload();
        if (!user.emailVerified && !invitationToken) {
          const pending = new Error("Please confirm your email address, then continue.");
          pending.code = "EMAIL_VERIFICATION_REQUIRED";
          throw pending;
        }
        try {
          await provisionAccount({ fbUser: user, turnstileToken });
        } catch (error) {
          return handleProvisionFailure(error);
        }
      }
    }

    // Update user in state
    setUser(user);
    return user;
  };

  const signup = async (email, password, companyName, turnstileToken) => {
    const response = await createUserWithEmailAndPassword(auth, email, password);
    return handleAuth(response, { companyName, turnstileToken });
  };

  const signin = async (email, password, turnstileToken) => {
    const response = await signInWithEmailAndPassword(auth, email, password);
    return handleAuth(response, { turnstileToken });
  };

  const signinWithProvider = async (name) => {
    // Get provider by name ("google", "facebook", etc)
    const provider = authProviders.find((p) => p.name === name).get();
    const response = await signInWithPopup(auth, provider);
    return handleAuth(response);
  };

  const signout = () => {
    return authSignOut(auth);
  };

  // SOUND-722: this makes us send mail from our domain and is unauthenticated,
  // so it carries a challenge like the other mail paths.
  const sendPasswordResetEmail = (email, turnstileToken) => {
    return client
      .post("users/forgot-password", { email, turnstileToken })
      .then(() => {});
  };

  const confirmPasswordReset = (password, code) => {
    // Get code from query string object
    const resetCode = code || getFromQueryString("oobCode");
    return authConfirmPasswordReset(auth, resetCode, password);
  };

  const updatePassword = (password) => {
    return authUpdatePassword(auth.currentUser, password);
  };

  // Update auth user and persist data to database
  // Call this function instead of multiple auth/db update functions
  const updateProfile = async (data) => {
    const { email, name, picture } = data;

    // Update auth email
    if (email) {
      await authUpdateEmail(auth.currentUser, email);
    }

    // Update built-in auth profile fields
    // These fields are renamed in `useFormatUser`, so when updating we
    // need to make sure to use their original names (`displayName`, `photoURL`, etc)
    if (name || picture) {
      let fields = {};
      if (name) fields.displayName = name;
      if (picture) fields.photoURL = picture;
      await authUpdateProfile(auth.currentUser, fields);
    }

    // Persist all data to the database
    const token = await auth.currentUser.getIdToken();
    await updateUser(user.uid, data, {
      id: user.uid,
      accessToken: token,
    });

    // Update user in state
    setUser(auth.currentUser);
  };

  useEffect(() => {
    // Subscribe to user on mount
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(false);
      }
    });

    // Unsubscribe on cleanup
    return () => unsubscribe();
  }, []);

  return {
    user: finalUser,
    signup,
    signin,
    signinWithProvider,
    signout,
    // SOUND-715: the signup flow is now resumable, so the form needs to drive
    // the steps between "account created" and "account provisioned".
    provisionAccount,
    resendVerification,
    refreshVerification,
    sendPasswordResetEmail,
    confirmPasswordReset,
    updatePassword,
    updateProfile,
  };
}

function useFormatUser(user) {
  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // Return if auth user is `null` (loading) or `false` (not authenticated)
    if (!user) return user;

    // Create an array of user's auth providers by id (["password", "google", etc])
    // Components can read this to prompt user to re-auth with the correct provider
    const providers = user.providerData.map(({ providerId }) => {
      return authProviders.find((p) => p.id === providerId).name;
    });

    return {
      // Include full auth user data
      ...user,
      // Alter the names of some fields
      name: user.displayName,
      picture: user.photoURL,
      // User's auth providers
      providers: providers,

      // // Add `planId` (starter, pro, etc) based on Stripe Price ID
      // ...(user.stripePriceId && {
      //   planId: getFriendlyPlanId(user.stripePriceId),
      // }),
      // // Add `planIsActive: true` if subscription status is active or trialing
      // planIsActive: ["active", "trialing"].includes(
      //   user.stripeSubscriptionStatus
      // ),
    };
  }, [user]);
}

function useMergeExtraData(user, { enabled }) {
  // SOUND-251 (#4): resolve the ID token with getIdToken() rather than reading
  // `user.accessToken` off the Firebase User object.
  //
  // `accessToken` is not part of the SDK's documented surface — it happens to be
  // exposed today. If it is ever undefined, useUser's own guard
  // (`if (!authUser?.accessToken) return null`) returns null, which this hook
  // reads as "auth is still loading" and the app sits on PageLoader forever. So
  // the failure mode is not a 401 that the interceptor retries — it is a
  // permanent loading state, which is the same white screen this ticket came
  // from wearing a different hat.
  //
  // Two other sites in this file were already converted to getIdToken(); this
  // was the one that was missed.
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // `user` is an object, `null` (loading) or `false` (signed out).
    if (!user) {
      setAccessToken(null);
      return undefined;
    }
    user
      .getIdToken()
      .then((token) => {
        if (!cancelled) setAccessToken(token);
      })
      .catch(() => {
        // Leave it null: the query stays disabled and the app keeps showing the
        // loader rather than firing an unauthenticated request.
        if (!cancelled) setAccessToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Get extra user data from database (API call via React Query)
  const authUser = user && accessToken ? { id: user.uid, accessToken } : null;
  // NOTE the ternary. `enabled && user && user.uid && accessToken` would
  // evaluate to `true`, not the uid — passing a boolean as the query key and
  // breaking the cache. The uid has to be returned explicitly.
  const { data, status, error } = useUser(
    enabled && user && accessToken ? user.uid : null,
    authUser
  );

  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // If disabled or no auth user (yet) then just return
    if (!enabled || !user) return user;

    switch (status) {
      case "success":
        // If successful, but `data` is `null`, that means user just signed up and the `createUser`
        // function hasn't populated the db yet. Return `null` to indicate auth is still loading.
        // The above call to `useUser` will re-render things once the data comes in.
        if (data === null) return null;
        // Return auth `user` merged with extra user `data`
        return { ...user, ...data };
      case "error":
        // Uh oh.. Let's at least show a helpful error.
        throw new Error(`
          Error: ${error.message}
          This happened while attempting to fetch extra user data from the database
          to include with the authenticated user. Make sure the database is setup or
          disable merging extra user data by setting MERGE_DB_USER to false.
        `);
      default:
        // We have an `idle` or `loading` status so return `null`
        // to indicate that auth is still loading.
        return null;
    }
  }, [user, enabled, data, status, error]);
}

// Connect analytics session to current user
function useIdentifyUser(user, { enabled }) {
  useEffect(() => {
    if (user && enabled) {
      analytics.identify(user.uid);
    }
  }, [user, enabled]);
}

// A Higher Order Component for requiring authentication
export const requireAuth = (Component, showLoader = true) => {
  return function RequireAuthHOC(props) {
    const navigate = useNavigate();
    // Get authenticated user
    const auth = useAuth();

    useEffect(() => {
      // auth.user will return as null while the object is loading
      if (auth.user === null) {
        return;
      }

      // auth.user will return as false if the user is not signed in
      // Redirect if not signed in
      if (auth.user === false) {
        navigate("/auth/signin");
        return;
      }
    }, [auth, navigate]);

    if (auth.user === null) {
      return showLoader ? <PageLoader /> : null;
    }

    // Render component now that we have user
    return <Component {...props} />;
  };
};

// Handle Firebase email link for reverting to original email after an email change
export const handleRecoverEmail = async (code) => {
  // Check that action code is valid
  const info = await checkActionCode(auth, code);
  // Revert to original email by applying action code
  await applyActionCode(auth, code);
  // Send password reset email so user can change their password in the case
  // that someone else got into their account and changed their email.
  await authSendPasswordResetEmail(auth, info.data.email);
  // Return original email so it can be displayed by calling component
  return info.data.email;
};

// Handle Firebase email link for verifying email
export const handleVerifyEmail = (code) => {
  return applyActionCode(auth, code);
};

const authProviders = [
  {
    id: "password",
    name: "password",
  },
  {
    id: "google.com",
    name: "google",
    get: () => new GoogleAuthProvider(),
  },
  {
    id: "facebook.com",
    name: "facebook",
    get: () => {
      const provider = new FacebookAuthProvider();
      provider.setCustomParameters({ display: "popup" });
      return provider;
    },
  },
  {
    id: "twitter.com",
    name: "twitter",
    get: () => new TwitterAuthProvider(),
  },
  {
    id: "github.com",
    name: "github",
    get: () => new GithubAuthProvider(),
  },
];

// Wait for Firebase user to be initialized before resolving promise
// and taking any further action (such as writing to the database)
const waitForFirebase = (uid) => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Ensure we have a user with expected `uid`
      if (user && user.uid === uid) {
        resolve(user); // Resolve promise
        unsubscribe(); // Prevent from firing again
      }
    });
  });
};

const getFromQueryString = (key) => {
  return queryString.parse(window.location.search)[key];
};
